import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class VersionService {
  // Get version from environment
  private readonly APP_VERSION = (environment as any).appVersion || '1.0.1750351403';
  
  // Storage keys
  private readonly STORAGE_VERSION_KEY = 'app_version';
  private readonly STORAGE_LAST_CHECK_KEY = 'last_version_check';
  
  // Minimum time between checks (24 hours in milliseconds)
  private readonly CHECK_INTERVAL = 24 * 60 * 60 * 1000;
  
  constructor(
    private router: Router,
    @Optional() private swUpdate: SwUpdate
  ) {}
  
  /**
   * Initialize version checking
   * Call this method in app initialization
   */
  initVersionCheck(): void {
    // Check version on startup, but with rate limiting
    this.checkVersionWithRateLimit();
    
    // Check when service worker detects an update
    if (this.swUpdate && this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe(() => {
        console.log('Service worker detected an update');
        if (confirm('A new version of the app is available. Load it now?')) {
          this.forceRefresh();
        }
      });
    }
  }
  
  /**
   * Check version with rate limiting to prevent refresh loops
   */
  private checkVersionWithRateLimit(): void {
    try {
      // Get stored version and last check time
      const storedVersion = localStorage.getItem(this.STORAGE_VERSION_KEY);
      const lastCheckStr = localStorage.getItem(this.STORAGE_LAST_CHECK_KEY);
      const lastCheck = lastCheckStr ? parseInt(lastCheckStr, 10) : 0;
      const now = Date.now();
      
      console.log(`Stored version: ${storedVersion}, Current version: ${this.APP_VERSION}`);
      
      // Always store current version
      localStorage.setItem(this.STORAGE_VERSION_KEY, this.APP_VERSION);
      
      // If no stored version, this is first run or localStorage was cleared
      if (!storedVersion) {
        console.log('No stored version found, storing current version');
        localStorage.setItem(this.STORAGE_LAST_CHECK_KEY, now.toString());
        return;
      }
      
      // Check if enough time has passed since last check
      const timeSinceLastCheck = now - lastCheck;
      if (timeSinceLastCheck < this.CHECK_INTERVAL) {
        console.log(`Skipping version check, last check was ${Math.round(timeSinceLastCheck / 1000 / 60)} minutes ago`);
        return;
      }
      
      // Update last check time
      localStorage.setItem(this.STORAGE_LAST_CHECK_KEY, now.toString());
      
      // If version mismatch and enough time has passed, prompt for refresh
      if (storedVersion !== this.APP_VERSION) {
        console.log(`Version mismatch - Stored: ${storedVersion}, Current: ${this.APP_VERSION}`);
        if (confirm('A new version of the app is available. Load it now?')) {
          this.forceRefresh();
        }
      }
    } catch (error) {
      console.error('Error checking version:', error);
    }
  }
  
  /**
   * Force a full page refresh
   */
  forceRefresh(): void {
    console.log('Forcing app refresh');
    window.location.reload();
  }
  
  /**
   * Get the current app version
   */
  getAppVersion(): string {
    return this.APP_VERSION;
  }
  
  /**
   * Manual refresh method that can be called from UI
   */
  manualRefresh(): void {
    console.log('Manual refresh triggered');
    this.forceRefresh();
  }
}
